import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, ChevronLeft, Save, MapPin, Percent, Globe, Clock, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Commercial Settings Page
 * Configure VAT rates per site and other commercial settings
 */
export default function CommercialSettings() {
  const navigate = useNavigate();
  
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => { fetchSites(); }, []);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commercial/sites`);
      setSites(res.data.sites || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateSite = async (siteId, field, value) => {
    setSaving(siteId);
    try {
      await axios.put(`${API}/commercial/sites/${siteId}`, { [field]: value });
      setSuccess(siteId);
      setTimeout(() => setSuccess(null), 2000);
      fetchSites();
    } catch (err) { console.error(err); }
    finally { setSaving(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-settings">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
          <div><h1 className="text-xl font-bold text-gray-900">Settings</h1><p className="text-sm text-gray-500">Commercial Management Configuration</p></div>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-4xl">
        {/* VAT Configuration */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Percent className="w-5 h-5 text-blue-600" /></div>
            <div><h3 className="font-semibold text-gray-900">VAT Configuration</h3><p className="text-sm text-gray-500">Configure VAT rates per site</p></div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-gray-400 animate-spin" /></div>
          ) : sites.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No sites configured</p>
              <button onClick={() => navigate("/admin/commercial/sites")} className="mt-2 text-sm text-blue-600 hover:underline">Add a site first</button>
            </div>
          ) : (
            <div className="space-y-4">
              {sites.map((site) => (
                <div key={site.site_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    <div><p className="font-medium text-gray-900">{site.name}</p><p className="text-xs text-gray-500">{site.code}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">VAT Rate:</label>
                      <div className="relative">
                        <input 
                          type="number" 
                          value={site.vat_rate} 
                          onChange={(e) => updateSite(site.site_id, "vat_rate", parseFloat(e.target.value))}
                          className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-right pr-6"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
                      </div>
                    </div>
                    {saving === site.site_id && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />}
                    {success === site.site_id && <CheckCircle className="w-4 h-4 text-green-500" />}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> VAT is calculated as an inclusive rate. For UK standard rate of 20%, 
              the VAT portion of a £120 gross payment would be £20, leaving £100 net.
            </p>
          </div>
        </div>

        {/* Currency Configuration */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><Globe className="w-5 h-5 text-green-600" /></div>
            <div><h3 className="font-semibold text-gray-900">Currency & Locale</h3><p className="text-sm text-gray-500">Currency settings per site</p></div>
          </div>

          {!loading && sites.length > 0 && (
            <div className="space-y-4">
              {sites.map((site) => (
                <div key={site.site_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    <p className="font-medium text-gray-900">{site.name}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">Currency:</label>
                      <select 
                        value={site.currency} 
                        onChange={(e) => updateSite(site.site_id, "currency", e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="GBP">GBP (£)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">Timezone:</label>
                      <select 
                        value={site.timezone} 
                        onChange={(e) => updateSite(site.site_id, "timezone", e.target.value)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="Europe/London">Europe/London</option>
                        <option value="Europe/Dublin">Europe/Dublin</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Links</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button onClick={() => navigate("/admin/commercial/sites")} className="p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100">
              <MapPin className="w-5 h-5 text-teal-600 mb-2" />
              <p className="text-sm font-medium">Manage Sites</p>
            </button>
            <button onClick={() => navigate("/admin/commercial/finance")} className="p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100">
              <Percent className="w-5 h-5 text-blue-600 mb-2" />
              <p className="text-sm font-medium">Finance Centre</p>
            </button>
            <button onClick={() => navigate("/admin/commercial/audit")} className="p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100">
              <Clock className="w-5 h-5 text-purple-600 mb-2" />
              <p className="text-sm font-medium">Audit Centre</p>
            </button>
            <button onClick={() => navigate("/admin/commercial")} className="p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100">
              <Settings className="w-5 h-5 text-gray-600 mb-2" />
              <p className="text-sm font-medium">Dashboard</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
